import dayjs from "dayjs";
import { isEmpty, orderBy } from "lodash";

export const getAccessToken = () => localStorage.getItem("access_token") || "";
export const getUser = () => localStorage.getItem("user") || "";

export const formatTel = (value: string) => {
  if (!value) return "";
  return value.replace(/^(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
};

export const formatDay = (value: any) => {
  if (!value) return "";
  // ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  return ["日", "月", "火", "水", "木", "金", "土"][new Date(value).getDay()];
};

export const handleUpdatePhoto = (element: any, elementOriginal: any) => {
  const originalAfter = elementOriginal?.after_photo;
  const newAfter = element?.after_photo;

  const hasAfterChanged =
    newAfter !== undefined &&
    newAfter !== null &&
    originalAfter?.after_photo_key &&
    newAfter.after_photo_key !== originalAfter.after_photo_key &&
    newAfter !== originalAfter;

  if (hasAfterChanged) {
    element.delete_after_photo = true;
  }

  const originalBefore = elementOriginal?.before_photo;
  const newBefore = element?.before_photo;

  const hasBeforeChanged =
    newBefore !== undefined &&
    newBefore !== null &&
    originalBefore?.before_photo_key &&
    newBefore.before_photo_key !== originalBefore.before_photo_key &&
    newBefore !== originalBefore;

  if (hasBeforeChanged) {
    element.delete_before_photo = true;
  }

  if (newAfter?.image_path || newAfter === undefined || newAfter === null) {
    delete element.after_photo;
  }

  if (newBefore?.image_path || newBefore === undefined || newBefore === null) {
    delete element.before_photo;
  }
};

export const mapWorkReportAttributes = (workReports: any[] = []) => {
  const reports = orderBy(workReports, (obj) => obj.id, "asc");
  const extras = reports.filter((item) => item.parent_id);

  return reports
    .filter((item) => !item.parent_id)
    .map((item) => ({
      ...item,
      extra_photos: !isEmpty(item.extra_photos)
        ? item.extra_photos
        : extras
            .filter((extra) => extra.parent_id === item.id)
            .map((extra) => ({
              id: extra.id,
              parent_id: extra.parent_id,
              before_photo: extra.before_photo,
              after_photo: extra.after_photo,
            })),
    }));
};

const extraPhotoPayload = (extra: any, original?: any) => {
  const cloned = { ...extra };
  handleUpdatePhoto(cloned, original);

  return {
    id: cloned.id,
    before_photo:
      cloned.before_photo_key ||
      (typeof cloned.before_photo === "string"
        ? cloned.before_photo
        : undefined),
    after_photo:
      cloned.after_photo_key ||
      (typeof cloned.after_photo === "string" ? cloned.after_photo : undefined),
    delete_before_photo: cloned.delete_before_photo,
    delete_after_photo: cloned.delete_after_photo,
  };
};

export const buildExtraPhotosPayload = (
  extraPhotos: any[] = [],
  originalReports: any[] = []
) => {
  return extraPhotos
    .map((extra) => {
      const original =
        extra.id &&
        originalReports.find(
          (item: any) =>
            item.id === extra.id ||
            item.extra_photos?.find((photo: any) => photo.id === extra.id)
        );
      const originalExtra =
        original?.id === extra.id
          ? original
          : original?.extra_photos?.find((photo: any) => photo.id === extra.id);

      return extraPhotoPayload(extra, originalExtra);
    })
    .filter((extra) => extra.id || extra.before_photo || extra.after_photo);
};

export const processWorkingHour = (
  isDateChanged: any,
  isTimeChanged: any,
  histories: any,
  historyTimeField: any,
  date: any,
  timeValue: any
) => {
  if (isDateChanged || isTimeChanged) {
    if (date && timeValue) {
      if (typeof timeValue === "string" && timeValue.includes("T")) {
        const decodedTime = decodeURIComponent(timeValue);
        return new Date(decodedTime);
      } else {
        return new Date(dayjs(`${date} ${timeValue}`).toISOString());
      }
    } else {
      return "";
    }
  } else if (Array.isArray(histories) && histories.length > 0) {
    const latest = histories
      .filter((h) => h[historyTimeField])
      .reduce((latest, current) => {
        return new Date(current[historyTimeField]) >
          new Date(latest[historyTimeField])
          ? current
          : latest;
      });

    return latest?.[historyTimeField] || "";
  } else {
    return "";
  }
};

export const getLatestTime = (entry: any): number => {
  return (
    ["return_order_date", "start_time", "end_time"]
      .map((field) => entry[field])
      .filter((t) => t && !isNaN(new Date(t).getTime()))
      .map((t) => new Date(t).getTime())
      .sort((a, b) => b - a)[0] ?? -Infinity
  );
};

export const getReturnOrder = (request_content: any, userId: number) => {
  const rawHistories = request_content?.return_date_histories;
  if (!rawHistories) return null;
  const histories = Array.isArray(rawHistories) ? rawHistories : [rawHistories];

  const returnOrderWorker =
    histories
      .filter((entry) => entry.user_id === userId)
      .sort((a, b) => getLatestTime(b) - getLatestTime(a))
      .at(0) || null;

  const returnDateAdmin = histories
    .filter((entry) => entry.user_id == null)
    .reduce<any | null>((latest, entry) => {
      if (!latest) return entry;
      return getLatestTime(entry) > getLatestTime(latest) ? entry : latest;
    }, null);

  return returnOrderWorker?.return_order_date
    ? returnOrderWorker
    : returnDateAdmin;
};

export const getLatestReturnOrderHistory = (histories: any) => {
  if (!Array.isArray(histories)) return null;

  const filtered = histories.filter((h) => h.return_order_date);
  if (filtered.length === 0) return null;

  return filtered.reduce((latest, current) => {
    return getLatestTime(current) > getLatestTime(latest) ? current : latest;
  });
};

const formatTimeString = (time: Date) =>
  `${time.getHours().toString().padStart(2, "0")}:${time
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

export const setTimeIfNotChanged = (
  isChangeTime: any,
  sourceTime: any,
  setter: (value: string) => void
) => {
  if (sourceTime && !isChangeTime) {
    const time = new Date(sourceTime);
    setter(formatTimeString(time));
  }
};
